/**
 * Weather overlay — canvas déšť/bouřka, CSS slunce/mlha.
 * pointer-events: none; nestrká se do kliků.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { readWeather, type AcmWeather } from '../lib/weather';

type Drop = {
  x: number;
  y: number;
  len: number;
  spd: number;
  w: number;
  a: number;
};

type Flake = {
  x: number;
  y: number;
  r: number;
  spd: number;
  drift: number;
  phase: number;
  a: number;
};

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function RainCanvas({ storm }: { storm: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = prefersReducedMotion();
    let raf = 0;
    let running = true;
    let drops: Drop[] = [];
    const wind = storm ? 3.4 : 0.7;

    const spawn = (w: number, h: number, scatter: boolean): Drop => ({
      x: Math.random() * (w + 120) - 40,
      y: scatter ? Math.random() * h : -20 - Math.random() * 80,
      len: storm ? 16 + Math.random() * 26 : 9 + Math.random() * 16,
      spd: storm ? 13 + Math.random() * 16 : 6.5 + Math.random() * 9,
      w: storm ? 1 + Math.random() * 1.1 : 0.65 + Math.random() * 0.55,
      a: 0.16 + Math.random() * (storm ? 0.42 : 0.32),
    });

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round((storm ? 0.18 : 0.11) * w);
      drops = Array.from({ length: Math.max(48, Math.min(count, storm ? 220 : 150)) }, () =>
        spawn(w, h, true),
      );
    };

    const draw = () => {
      if (!running) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = 'round';
      for (const d of drops) {
        ctx.strokeStyle = storm
          ? `rgba(186, 206, 230, ${d.a})`
          : `rgba(174, 198, 224, ${d.a})`;
        ctx.lineWidth = d.w;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x + wind * (d.len / 12), d.y + d.len);
        ctx.stroke();
        if (!reduced) {
          d.y += d.spd;
          d.x += wind * 0.55;
          if (d.y > h + 20 || d.x > w + 40) {
            Object.assign(d, spawn(w, h, false));
          }
        }
      }
      if (!reduced && !document.hidden) raf = requestAnimationFrame(draw);
    };

    const onVis = () => {
      if (document.hidden || reduced) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [storm]);

  return <canvas ref={ref} className='acm-weather-canvas' aria-hidden />;
}

function SnowCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = prefersReducedMotion();
    let raf = 0;
    let running = true;
    let flakes: Flake[] = [];
    let tick = 0;

    const spawn = (w: number, h: number, scatter: boolean): Flake => ({
      x: Math.random() * (w + 40) - 20,
      y: scatter ? Math.random() * h : -12 - Math.random() * 40,
      r: 1.1 + Math.random() * 2.6,
      spd: 0.35 + Math.random() * 0.85,
      drift: 0.35 + Math.random() * 0.7,
      phase: Math.random() * Math.PI * 2,
      a: 0.28 + Math.random() * 0.45,
    });

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(0.09 * w);
      flakes = Array.from({ length: Math.max(40, Math.min(count, 130)) }, () =>
        spawn(w, h, true),
      );
    };

    const draw = () => {
      if (!running) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      tick += 1;
      for (const f of flakes) {
        ctx.beginPath();
        ctx.fillStyle = `rgba(236, 244, 252, ${f.a})`;
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
        if (!reduced) {
          f.y += f.spd;
          f.x += Math.sin(tick * 0.012 + f.phase) * f.drift;
          if (f.y > h + 12) Object.assign(f, spawn(w, h, false));
        }
      }
      if (!reduced && !document.hidden) raf = requestAnimationFrame(draw);
    };

    const onVis = () => {
      if (document.hidden || reduced) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return <canvas ref={ref} className='acm-weather-canvas' aria-hidden />;
}

export function WeatherOverlay() {
  const [kind, setKind] = useState<AcmWeather>(() =>
    typeof document !== 'undefined' ? readWeather() : 'off',
  );

  useEffect(() => {
    const onWeather = (e: Event) => {
      const next = (e as CustomEvent<{ weather?: AcmWeather }>).detail?.weather;
      if (next) setKind(next);
    };
    window.addEventListener('acm-weather', onWeather);
    return () => window.removeEventListener('acm-weather', onWeather);
  }, []);

  if (kind === 'off' || typeof document === 'undefined') return null;

  return createPortal(
    <div className='acm-weather' data-kind={kind} aria-hidden>
      {(kind === 'rain' || kind === 'storm') && <RainCanvas storm={kind === 'storm'} />}
      {kind === 'snow' && (
        <>
          <div className='acm-weather-snow-wash' />
          <SnowCanvas />
        </>
      )}
      {kind === 'storm' && (
        <>
          <div className='acm-weather-storm-wash' />
          <div className='acm-weather-flash' />
          <div className='acm-weather-fog acm-weather-fog-storm' />
        </>
      )}
      {kind === 'sun' && (
        <>
          <div className='acm-weather-sun-wash' />
          <div className='acm-weather-sun-rays' />
          <div className='acm-weather-sun-disk' />
        </>
      )}
      {kind === 'fog' && (
        <>
          <div className='acm-weather-fog-veil' />
          <div className='acm-weather-fog acm-weather-fog-1' />
          <div className='acm-weather-fog acm-weather-fog-2' />
          <div className='acm-weather-fog acm-weather-fog-3' />
        </>
      )}
    </div>,
    document.body,
  );
}
